/**
 * StaffDevices (STAFF-DEVICE-LOCK / FE-3) — admin manages one staff member's
 * approved-device list.
 *
 * Modal-shaped surface, cloned from `../DepartmentAssignment`'s pattern.
 * Opened per member from Manage Staff (wired by FE-4, a later task) —
 * self-contained here behind the FROZEN prop contract:
 *
 *   <StaffDevices
 *     isOpen={bool}
 *     onClose={fn}
 *     member={{ member_id, first_name, last_name }}
 *     onChanged={(summaryForMember: {member_id, locked, approved_count}) => void}
 *     isSelf={bool}       // is `member` the currently signed-in admin? (R4/D2, D3)
 *     isSoleAdmin={bool}  // is the signed-in admin the only ADMIN in the org? (R4/D2)
 *   />
 *
 * Single source of truth: every badge, the state line, the cap note, and the
 * `onChanged` chip payload are derived from the ONE `DeviceListResponse`
 * envelope returned by the last successful call (list / approve / remove all
 * return the identical shape) — never a second fetch, never a locally
 * re-derived count. `isSelf`/`isSoleAdmin` are likewise derived by the caller
 * from the staff list Manage Staff already loads — no dedicated fetch here.
 *
 * No native browser dialogs — every confirmation goes through the shared
 * `ConfirmationModal`. Copy below is PO-owned verbatim text from
 * build-plan.md §FE-3 / stories.md S2/S3 — do not improvise new microcopy.
 * Exception: the first-approval confirmation was corrected in the
 * STAFF-DEVICE-LOCK rework (r1, business-gate defect) — it previously
 * claimed other devices "will be signed out", which enforcement does not
 * do (session resolution only, at next sign-in / page refresh).
 *
 * R4/D2 + D3 (business-gate deferred items, sole-admin lockout guards): a
 * WARNING, never a block — the owner chose the rescue script over a hard
 * guard, so the admin may still proceed either way.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import { formatDateIST } from '../../../utils/formatIST';
import { fetchMemberDevices, approveDevice, removeDevice } from '../../../services/api/deviceApi';
import { getOrCreateDeviceId } from '../../../utils/deviceId';
import './StaffDevices.css';

// R4/D2 — shown only when approving would flip the signed-in admin's OWN
// row from unlocked to locked and no other admin exists in the org to
// approve a replacement if this device is later lost. Plain language: what
// the risk is (no one else can help) and what protects them (a 2nd admin).
const SOLE_ADMIN_LOCKOUT_MESSAGE =
  "You're the only admin in this organisation. From now on, only devices you approve can sign in to your account — other devices will be blocked the next time they sign in or refresh the page. If this device is lost or its browser data is cleared, no one else here can approve another device for you. A second admin account protects you against that.";

// R5 (business gate C2) — Remove, branch 1: the row is not approved. Nothing
// about access changes whether the member is locked or unlocked, whose row
// it is, or whether it's this browser — so one message, no further
// branching. Replaces the old generic string for this case.
const REMOVE_UNAPPROVED_MESSAGE =
  "This device isn't approved, so removing it doesn't change who can sign in. It will reappear here the next time that device tries to sign in.";

// ── Pure helpers (single derivation site each — never re-derived inline) ──

/** Rough "Chrome on Windows" style hint parsed from a recorded user_agent. */
function suggestDeviceName(userAgent) {
  if (!userAgent || typeof userAgent !== 'string') return '';

  let browser = 'Browser';
  if (/Edg\//.test(userAgent)) browser = 'Edge';
  else if (/OPR\//.test(userAgent)) browser = 'Opera';
  else if (/Chrome\//.test(userAgent) || /CriOS\//.test(userAgent)) browser = 'Chrome';
  else if (/Firefox\//.test(userAgent)) browser = 'Firefox';
  else if (/Safari\//.test(userAgent)) browser = 'Safari';

  // iPhone/iPad UAs always contain "like Mac OS X" — check them BEFORE the
  // Mac test, or every iOS device gets suggested as "on Mac".
  let os = 'device';
  if (/Windows/.test(userAgent)) os = 'Windows';
  else if (/iPhone|iPad|iOS/.test(userAgent)) os = 'iOS';
  else if (/Mac OS X|Macintosh/.test(userAgent)) os = 'Mac';
  else if (/Android/.test(userAgent)) os = 'Android';
  else if (/Linux/.test(userAgent)) os = 'Linux';

  return `${browser} on ${os}`;
}

/** Display label for an unapproved row's name-or-hint column. */
function deviceHint(userAgent) {
  return suggestDeviceName(userAgent) || 'Unknown device';
}

/** Badge label / helper text / colour class — one place, used everywhere. */
function getBadgeMeta(device, locked) {
  if (device.status === 'approved') {
    return {
      label: 'Approved',
      hint: 'can sign in',
      className:
        'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 border-emerald-400 dark:border-emerald-700',
    };
  }
  if (locked) {
    return {
      label: 'Blocked — not approved',
      hint: 'cannot sign in until you approve it',
      className:
        'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 border-red-400 dark:border-red-700',
    };
  }
  return {
    label: 'Not approved yet',
    hint: null,
    className:
      'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-600',
  };
}

/** Approved-row count derived from the ONE envelope — never tracked separately. */
function countApproved(envelope) {
  return (envelope?.devices || []).filter((d) => d.status === 'approved').length;
}

export default function StaffDevices({ isOpen, onClose, member, onChanged, isSelf = false, isSoleAdmin = false }) {
  const memberId = member?.member_id;
  const memberName = useMemo(() => {
    const name = `${member?.first_name || ''} ${member?.last_name || ''}`.trim();
    return name || memberId || 'this member';
  }, [member, memberId]);

  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Inline approve-row editing (one row at a time).
  const [approvingId, setApprovingId] = useState(null);
  const [nameDraft, setNameDraft] = useState('');
  const [serverDuplicate, setServerDuplicate] = useState(false);
  const [busyDeviceId, setBusyDeviceId] = useState(null);

  // Single ConfirmationModal instance driven by whichever action asked for it.
  const [pendingConfirm, setPendingConfirm] = useState(null);

  // Ignore a stale in-flight response if a newer load has since started
  // (single-flight guard — never let an out-of-order reply overwrite fresher
  // state or flip a badge back).
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    if (!memberId) return;
    const seq = ++loadSeq.current;
    setStatus('loading');
    setError(null);
    const res = await fetchMemberDevices(memberId);
    if (seq !== loadSeq.current) return; // superseded
    if (res.success) {
      setData(res.data);
      setStatus('ready');
    } else {
      setStatus('error');
      setError(res.error || 'Failed to load devices.');
    }
  }, [memberId]);

  // Fetch exactly once per open (stable deps — isOpen/memberId only, no
  // object/array recreated every render). Never polls.
  useEffect(() => {
    if (!isOpen || !memberId) return;
    setApprovingId(null);
    setNameDraft('');
    setServerDuplicate(false);
    setBusyDeviceId(null);
    setPendingConfirm(null);
    load();
  }, [isOpen, memberId, load]);

  const notifyChanged = useCallback((envelope) => {
    if (!envelope) return;
    onChanged?.({
      member_id: envelope.member_id,
      locked: envelope.locked,
      approved_count: countApproved(envelope),
    });
  }, [onChanged]);

  const isDuplicateName = useCallback((name, deviceId) => {
    const trimmed = name.trim().toLowerCase();
    if (!trimmed) return false;
    return (data?.devices || []).some(
      (d) => d.device_id !== deviceId && (d.name || '').trim().toLowerCase() === trimmed
    );
  }, [data]);

  const startApprove = (device) => {
    setApprovingId(device.device_id);
    setNameDraft(suggestDeviceName(device.user_agent));
    setServerDuplicate(false);
  };

  const cancelApprove = () => {
    setApprovingId(null);
    setNameDraft('');
    setServerDuplicate(false);
  };

  const doApprove = async (device, name) => {
    setPendingConfirm(null);
    setBusyDeviceId(device.device_id);
    const res = await approveDevice(memberId, device.device_id, name);
    setBusyDeviceId(null);
    if (res.success) {
      setData(res.data);
      setApprovingId(null);
      setNameDraft('');
      setServerDuplicate(false);
      notifyChanged(res.data);
    } else if (res.code === 'DEVICE_NAME_DUPLICATE') {
      // Server caught a race the inline pre-check missed — stay in the row
      // editor, same message as the client-side check.
      setServerDuplicate(true);
    } else {
      // Unexpected failure (e.g. the row was removed concurrently) — fall
      // back to the same error state as a failed list load, with Retry.
      setStatus('error');
      setError(res.error || 'Failed to approve device.');
    }
  };

  const requestApprove = (device) => {
    const trimmed = nameDraft.trim();
    if (!trimmed || isDuplicateName(trimmed, device.device_id)) return;

    const isFirstApproval = !!data && !data.locked;
    // R4/D2 — this approval would lock the signed-in admin's OWN account and
    // there is no other admin to rescue them if this device is later lost.
    const isSoleAdminLockout = isFirstApproval && isSelf && isSoleAdmin;
    setPendingConfirm({
      title: 'Approve this device',
      // R1 fix (business gate defect 1): enforcement only happens at session
      // resolution (next sign-in / page refresh) — a device already signed
      // in keeps working until then. This copy must never claim an
      // immediate sign-out; it states the real trigger moment instead.
      message: isSoleAdminLockout
        ? SOLE_ADMIN_LOCKOUT_MESSAGE
        : isFirstApproval
          ? `From now on ${memberName} can only sign in from devices you approve. Other devices will be blocked the next time they sign in or refresh the page.`
          : `${memberName} will be able to sign in from this device.`,
      icon: isSoleAdminLockout ? '⚠️' : undefined,
      ctaLabel: 'Approve',
      variant: 'default',
      onConfirm: () => doApprove(device, trimmed),
    });
  };

  const doRemove = async (device) => {
    setPendingConfirm(null);
    setBusyDeviceId(device.device_id);
    const res = await removeDevice(memberId, device.device_id);
    setBusyDeviceId(null);
    if (res.success) {
      setData(res.data);
      notifyChanged(res.data);
    } else {
      setStatus('error');
      setError(res.error || 'Failed to remove device.');
    }
  };

  const requestRemove = (device) => {
    // R5 (business gate C2) — the ONE rule: removing a row only changes
    // access when the row is APPROVED (an approved row exists only on a
    // locked member, so `locked` collapses into this one check). This guard
    // must run FIRST — that ordering is the fix.
    const isApprovedRow = !!data?.locked && device.status === 'approved';
    const isLastApproved = isApprovedRow && countApproved(data) <= 1;
    // R4/D3 — this row is the device THIS browser is signed in from right
    // now. Say so explicitly instead of the generic wording, since removing
    // it can cut the acting admin's own access (at their next sign-in or
    // page refresh, never immediately).
    const isThisDevice = device.device_id === getOrCreateDeviceId();
    let message;
    if (!isApprovedRow) {
      message = REMOVE_UNAPPROVED_MESSAGE;
    } else if (isThisDevice && isLastApproved) {
      message =
        "This is the device you're using right now, and it's the only approved device. Removing it lets this member sign in from any device again, including this one — it will reappear in the list the next time it signs in.";
    } else if (isLastApproved) {
      message =
        'This device will be removed from the list and will reappear here on its next sign-in attempt — after this, this member will be able to sign in from any device again.';
    } else if (isThisDevice && isSelf) {
      message =
        "This is the device you're using right now, and it's your own account. It keeps working until your next sign-in or page refresh — after that this device is blocked, and you'd need one of your other approved devices to approve it again.";
    } else if (isThisDevice) {
      message =
        `You're using this device right now, but you're removing it from ${memberName}'s list — your own access isn't affected. ${memberName} won't be able to sign in from this device until you approve it again, from their next sign-in or page refresh.`;
    } else {
      // M6 — approved, others remain, not this browser (R5, new).
      message =
        `${memberName} can keep using this device until their next sign-in or page refresh — after that they can't sign in from it until you approve it again. Their other approved devices aren't affected.`;
    }
    setPendingConfirm({
      title: 'Remove this device',
      message,
      ctaLabel: 'Remove',
      variant: 'danger',
      onConfirm: () => doRemove(device),
    });
  };

  if (!isOpen || !member) return null;

  const locked = !!data?.locked;
  const capacity = data?.capacity;
  const atCap = !!capacity && capacity.used >= capacity.max;
  const atCapAllApproved = atCap && countApproved(data) === capacity.used;
  const devices = data?.devices || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-xl overflow-hidden text-slate-900 dark:text-slate-100">
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/40">
          <h2 className="text-base font-bold text-teal-900 dark:text-teal-100">
            Devices — {memberName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            Close
          </button>
        </div>

        <div className="p-5 space-y-3">
          {status === 'ready' && (
            <p className="text-sm text-slate-700 dark:text-slate-200">
              {locked
                ? 'Only the approved devices below can sign in.'
                : 'This member can sign in from any device. Approve a device below to allow only approved devices.'}
            </p>
          )}

          {status === 'ready' && atCap && !atCapAllApproved && (
            <div className="text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md p-2">
              {`This list holds ${capacity.max} devices. A new device replaces the oldest one that isn't approved yet — approved devices are never replaced.`}
            </div>
          )}

          {status === 'ready' && atCap && atCapAllApproved && (
            <div className="text-sm font-medium text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-400 dark:border-amber-700 rounded-md p-2">
              {`This list is full — all ${capacity.max} devices are approved. Until you remove one, a new device can't sign in and won't appear here.`}
            </div>
          )}

          {status === 'error' && (
            <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-400 dark:border-red-700 rounded-md p-3 flex items-center justify-between gap-3">
              <span>{error || "Something went wrong loading this member's devices."}</span>
              <button
                type="button"
                onClick={load}
                className="text-sm font-semibold px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 shrink-0"
              >
                Retry
              </button>
            </div>
          )}

          {(status === 'loading' || status === 'idle') && (
            <div className="space-y-2" aria-busy="true" aria-label="Loading devices">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="animate-pulse h-14 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800"
                />
              ))}
            </div>
          )}

          {status === 'ready' && devices.length === 0 && (
            <div className="text-sm text-slate-600 dark:text-slate-300 italic py-4 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-md">
              No sign-ins recorded yet. Ask them to sign in once, then approve their device here.
            </div>
          )}

          {status === 'ready' && devices.length > 0 && (
            <div className="staff-devices-list space-y-2">
              {devices.map((device) => {
                const badge = getBadgeMeta(device, locked);
                const isBusy = busyDeviceId === device.device_id;
                const isEditing = approvingId === device.device_id;
                const duplicateNow = isEditing
                  && !!nameDraft.trim()
                  && (isDuplicateName(nameDraft, device.device_id) || serverDuplicate);

                return (
                  <div
                    key={device.device_id}
                    className="p-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${badge.className}`}
                        title={badge.hint || undefined}
                      >
                        {badge.label}
                      </span>
                      <span className="text-sm font-medium flex-1 min-w-0 truncate">
                        {device.status === 'approved'
                          ? (device.name || deviceHint(device.user_agent))
                          : deviceHint(device.user_agent)}
                      </span>
                      <span className="text-xs text-slate-600 dark:text-slate-300 shrink-0">
                        Last seen {formatDateIST(device.last_seen_date) || '—'}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {device.status !== 'approved' && !isEditing && (
                        <button
                          type="button"
                          onClick={() => startApprove(device)}
                          disabled={isBusy}
                          className="text-xs font-medium px-2.5 py-1 rounded-md border border-teal-500 dark:border-teal-600 bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
                        >
                          {isBusy ? '…' : 'Approve'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => requestRemove(device)}
                        disabled={isBusy}
                        className="text-xs font-medium px-2.5 py-1 rounded-md border border-red-400 dark:border-red-700 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-60"
                      >
                        {isBusy ? '…' : 'Remove'}
                      </button>
                    </div>

                    {isEditing && (
                      <div className="mt-2 space-y-1">
                        <label
                          className="text-xs font-semibold text-slate-700 dark:text-slate-200"
                          htmlFor={`device-name-${device.device_id}`}
                        >
                          Device name
                        </label>
                        <input
                          id={`device-name-${device.device_id}`}
                          type="text"
                          value={nameDraft}
                          maxLength={40}
                          onChange={(e) => { setNameDraft(e.target.value); setServerDuplicate(false); }}
                          className="w-full text-sm px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                          placeholder="Name this device"
                        />
                        {!nameDraft.trim() && (
                          <p className="text-xs text-amber-700 dark:text-amber-300">
                            Enter a name for this device
                          </p>
                        )}
                        {duplicateNow && (
                          <p className="text-xs text-red-700 dark:text-red-300">
                            A device with this name already exists for this member.
                          </p>
                        )}
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => requestApprove(device)}
                            disabled={isBusy || !nameDraft.trim() || duplicateNow}
                            className="text-xs font-semibold px-2.5 py-1 rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={cancelApprove}
                            className="text-xs font-medium px-2.5 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t-2 border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/30">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold px-3 py-1.5 rounded-md bg-teal-600 text-white hover:bg-teal-700"
          >
            Close
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!pendingConfirm}
        title={pendingConfirm?.title}
        message={pendingConfirm?.message}
        ctaLabel={pendingConfirm?.ctaLabel}
        variant={pendingConfirm?.variant}
        icon={pendingConfirm?.icon || (pendingConfirm?.variant === 'danger' ? '⚠️' : '✅')}
        onConfirm={() => pendingConfirm?.onConfirm?.()}
        onCancel={() => setPendingConfirm(null)}
      />
    </div>
  );
}
